import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ENDED_TO_IDLE_MS } from '../../lib/endedToIdle';
import { Outlet, useOutletContext } from 'react-router-dom';
import { hasScreenAudioUnlockHint, rememberScreenAudioUnlocked } from '../../lib/screenAudioUnlock';
import { useActiveSessionId } from '../../lib/useActiveSession';
import { useGameState } from '../../lib/useGameState';
import { useLayoutPreview } from '../../lib/layoutPreviewContext';
import type { GameState, Participant } from '../../lib/types';
import ScreenNoSessionView from './ScreenNoSessionView';
import ScreenAudioConsentBar from './ScreenAudioConsentBar';
import { useScreenStageAudio } from './useScreenStageAudio';

export interface ScreenOutletContext {
  sessionId: string;
  state: GameState | null;
  participants: Participant[];
  sorted: Participant[];
  /** Rilettura REST (fallback se Realtime su `prompt` è in ritardo). */
  refresh: () => Promise<void>;
}

export function useScreenContext(): ScreenOutletContext {
  return useOutletContext<ScreenOutletContext>();
}

export default function ScreenLayout() {
  const { sessionId, loading } = useActiveSessionId();
  const { state, participants, refresh } = useGameState(sessionId);
  const layoutPreview = useLayoutPreview();

  const [audioPrimed, setAudioPrimed] = useState(false);
  const unlockRanRef = useRef(false);

  useEffect(() => {
    unlockRanRef.current = false;
  }, [sessionId]);

  const { primeAudioInUserGesture, flushPendingStingersInUserGesture, playConsentDemoInUserGesture } =
    useScreenStageAudio(state, {
      enabled: !!sessionId && !loading && !layoutPreview,
      audioPrimed,
    });

  const showAudioConsentChrome = !!sessionId && !loading && !audioPrimed && !layoutPreview;
  const resumeAudioHint = showAudioConsentChrome && hasScreenAudioUnlockHint();

  const unlockAudioFromUserGesture = useCallback(() => {
    if (unlockRanRef.current) return;
    unlockRanRef.current = true;
    playConsentDemoInUserGesture();
    primeAudioInUserGesture();
    flushPendingStingersInUserGesture();
    rememberScreenAudioUnlocked();
    setAudioPrimed(true);
  }, [playConsentDemoInUserGesture, primeAudioInUserGesture, flushPendingStingersInUserGesture]);

  /** Dopo un primo consenso nella stessa scheda: al reload basta un tocco ovunque (policy browser). */
  useEffect(() => {
    if (!showAudioConsentChrome || !resumeAudioHint) return;

    let consumed = false;
    const run = () => {
      if (consumed) return;
      consumed = true;
      unlockAudioFromUserGesture();
    };

    const onPointerDown = () => run();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (consumed) return;
      e.preventDefault();
      run();
    };

    window.addEventListener('pointerdown', onPointerDown, { capture: true });
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, { capture: true });
      window.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [showAudioConsentChrome, resumeAudioHint, unlockAudioFromUserGesture]);

  const sorted = useMemo(
    () => [...participants].sort((a, b) => a.slot - b.slot),
    [participants],
  );

  /** Dopo ENDED, forza round-tick a tempo per DISMISS_TO_IDLE (anche senza vincitore / senza regia). */
  useEffect(() => {
    if (layoutPreview) return;
    if (!sessionId || state?.status !== 'ENDED') return;

    const baseMs = state.updated_at ? new Date(state.updated_at).getTime() : Date.now();
    const delay = Math.max(0, baseMs + ENDED_TO_IDLE_MS - Date.now());

    const id = window.setTimeout(() => {
      void fetch('/api/jobs/round-tick', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      }).catch(() => {});
    }, delay);

    return () => window.clearTimeout(id);
  }, [layoutPreview, sessionId, state?.status, state?.updated_at]);

  if (loading) return <div className="appLoading">CONNESSIONE...</div>;
  if (!sessionId) {
    return <ScreenNoSessionView />;
  }

  const ctx: ScreenOutletContext = {
    sessionId,
    state,
    participants,
    sorted,
    refresh,
  };

  return (
    <>
      {showAudioConsentChrome ? (
        <ScreenAudioConsentBar onConsent={unlockAudioFromUserGesture} />
      ) : null}
      <Outlet context={ctx} />
    </>
  );
}
